import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from '../theme';
import { HandlingStripFace, primaryBandStyle, StufeChip, SkeletonBlock, SenderPill } from './shared';
import { FormPill } from './FormPill';
import { pillFace, pillStyle } from './SecondaryAxisPills';
import { overlayChipStyle } from './overlayEditorChrome';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { ClerkTrace } from './ClerkTrace';
import { DecisionBadge } from './DecisionBadge';
import { approvedWord, approvedKindWord } from '../services/pressVerdict';
import type { DraftKind } from '../types';
import { FristGenanntPill } from './FristGenanntPill';
import { useRowPillVisibility } from '../hooks/useRowPillVisibility';
import {
  rowPillKinds, parseHiddenRowPills, formatHiddenRowPills, withRowPillHidden, DEFAULT_HIDDEN_ROW_PILLS,
  ATTRIBUTE_KIND_PREFIX, type RowPillKind,
} from '../services/rowPillKinds';
import { getRowPillSurvey, type RowPillSurvey } from '../services/api';
import { formatBackendError } from '../utils/formatError';

// <purpose>„Hinweise in der Liste" — the Einstellungen › Workflow section directly under the
// row-layout lever (P2 Task R4, 2026-09-18, operator board 650688/01 Variant A). Three blocks, each
// answering a different person's question:
//   Vorgabe (admin)   — what a clerk who has never touched this sees. Rides the page's ordinary
//                       single-key config lane (UI_ROW_PILLS_HIDDEN), exactly like the lever above.
//   Meine Liste (all) — this clerk's own list, stored per user and saved on the spot.
//   Nutzung (admin)   — how many clerks keep each hint on, so a default is set on evidence rather
//                       than on taste.
// Under each grid the same two static preview rows, in the row-layout lever's grammar
// (HandlingStripFace + the tenant's served words, invented subjects), where a hidden hint FADES
// rather than disappears — the whole point of a preview is to show what is being switched
// off. The preview follows the tenant's SAVED row layout (eyeball finding 2, 2026-09-24): under
// strip_abbr the strip carries the served icon or letters and line 2 carries no handling badge
// (the strip already states it); under strip_label the strip is a narrow colour band and line 2
// carries the filled label badge — never a hard-coded strip_label regardless of what was
// saved. Both block titles carry the same „frozen" chip the Vokabular card uses (board 04, A):
// „Vorgabe" reads whose default it is, „Nur ich" that the personal block never touches the
// tenant.</purpose>
// <dependencies>services/rowPillKinds (THE registry — which hints exist, their German labels and
// their order) · hooks/useRowPillVisibility (the clerk's own list + the PUT) · useCategoryMeta (the
// served words the preview speaks, and the saved rowLayout) · getRowPillSurvey ·
// overlayEditorChrome's overlayChipStyle (the block-title chips).</dependencies>
// <constraints>The checkbox is the INVERSE of what is stored: checked = the hint shows, and the key
// holds what is HIDDEN. Both grids render the same German labels, so each checkbox carries an
// aria-label suffixed with its block — two identically-named controls on one page is an
// accessibility defect before it is a test problem.
// Layout-Stability: the feedback slot is always mounted at a fixed height with `role="status"`; the
// preview never removes a faded hint; the survey's own block keeps its intro line whether or not
// there is anything to survey; the chip sits inside the block-title line, never a row of its own.
// The survey is fetched ONLY behind the admin gate — a clerk without it must not even probe an
// endpoint they are not allowed to read.</constraints>

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
  letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
  borderBottom: `1px solid ${theme.colors.bgLight}`,
};
const BLOCK_TITLE: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 8, fontFamily: theme.font.family,
};
const HELPER: React.CSSProperties = {
  fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4,
  fontFamily: theme.font.family, margin: '8px 0 0',
};
const GRID: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6,
};
const CHECKBOX_ROW: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
  color: theme.colors.navy, fontFamily: theme.font.family, cursor: 'pointer',
};

function btnStyle(kind: 'primary' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500, fontFamily: theme.font.family,
  };
  return kind === 'primary'
    ? { ...base, background: theme.colors.navy, color: theme.colors.white }
    : { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}
function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: theme.borderRadius.sm, fontSize: 13, width: '100%',
    boxSizing: 'border-box',
    background: isError ? '#fbe9e9' : '#e9f6ec',
    color: isError ? theme.colors.error : '#1f7a3d',
  };
}

// The opacity a hidden hint wears in the preview. A number, not a removal: „so sieht die Zeile
// ohne diesen Hinweis aus" is only legible if the hint is still where it was.
const FADED = 0.28;

// A kind's label floors to '' while cm.configLoading (rowPillKinds.ts, `readsFace`) for the one
// def whose word is served (`eingang_bestaetigt`) — this renders the shimmer for that beat rather
// than an empty checkbox row, never the raw key `resolveFace` would otherwise answer (fix wave
// 2026-09-27, item 8). Every other kind's label is a plain code constant or already-resolved
// served noun, so it never hits this branch.
const RowPillLabel: React.FC<{ label: string }> = ({ label }) =>
  label ? <>{label}</> : <SkeletonBlock width={72} height={14} borderRadius={4} />;

// ─── The grid ──────────────────────────────────────────────────────────────────────────────────
const KindGrid: React.FC<{
  kinds: readonly RowPillKind[];
  hidden: readonly string[];
  /** Distinguishes the two identically-labelled grids for assistive technology. */
  block: string;
  disabled: boolean;
  onToggle: (id: string, visible: boolean) => void;
}> = ({ kinds, hidden, block, disabled, onToggle }) => (
  <div style={GRID}>
    {kinds.map(kind => {
      const visible = !hidden.includes(kind.id);
      return (
        <label key={kind.id} data-testid={`row-pill-kind-${kind.id}-${block}`} style={{ ...CHECKBOX_ROW, opacity: disabled ? 0.5 : 1 }}>
          <input
            type="checkbox"
            aria-label={`${kind.labelDe} (${block})`}
            checked={visible}
            disabled={disabled}
            onChange={e => onToggle(kind.id, e.target.checked)}
          />
          <RowPillLabel label={kind.labelDe} />
        </label>
      );
    })}
  </div>
);

// ─── The preview ───────────────────────────────────────────────────────────────────────────────
// Two static rows in the list's own grammar, each drawn as the list's row 2 + row 3 (ROW
// COMPOSITION v1, part 7 fix loop, 2026-10-12). Row A is a mail as it ARRIVES (the classification,
// the plum pill and the info pills), row B the same kind of mail once it has been DECIDED (the
// Bearbeitung group on row 3 right) — so every registry kind appears exactly once, on the line and
// in the order it really rides, and the never-hideable signals (the handling badge, the approved-as
// badge) stand at full opacity, which is itself the honest statement that they cannot be switched
// off.
// NO DATES on either row since ruling R33 (2026-09-19): the rows they stand for carry none, and a
// preview that kept one would teach a grammar the list no longer speaks.
// THE MARKET PARTNER is the one kind that appears in BOTH rows, because its two states are what a
// clerk is switching on or off: dashed = an unconfirmed PROPOSAL from the registry, solid = an
// established partner (rulings R33a/R33b — the border says nothing about the ticket, and the pill
// is outlined in both states so it never competes with the handling badge). One preference
// governs both specimens, so they fade together — see the `slot` helper's testId suffix, which is
// what keeps the canonical „row-pill-preview-<id>" testid unique while the kind is shown twice.
const PreviewRow: React.FC<{
  subject: string;
  stripValue: string | null;
  /** The band's paint (primaryBandStyle): the served hue, or the slate tint for the unclassified value. */
  stripPaint: React.CSSProperties;
  /** Row 2 — the classification group. */
  line2: React.ReactNode;
  /** Row 3 left — the plum pill first, then the info pills. */
  line3Left: React.ReactNode;
  /** Row 3 right — the Bearbeitung group (right-aligned, as on the list). */
  line3Right: React.ReactNode;
}> = ({ subject, stripValue, stripPaint, line2, line3Left, line3Right }) => {
  const cm = useCategoryMeta();
  const layout = cm.rowLayout;
  return (
    <div
      data-testid="row-pill-preview-row"
      style={{
        display: 'flex', alignItems: 'stretch',
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.white,
        overflow: 'hidden',
      }}
    >
      <div style={{
        width: theme.stripWidth[layout], flexShrink: 0, ...stripPaint,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <HandlingStripFace value={stripValue} layout={layout} />
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: '6px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: theme.colors.navy, fontFamily: theme.font.family }}>
          {subject}
        </div>
        <div data-testid="row-pill-preview-line-2" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, minHeight: 18, flexWrap: 'wrap' }}>
          {line2}
        </div>
        <div data-testid="row-pill-preview-line-3" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, minHeight: 18, flexWrap: 'wrap' }}>
          {line3Left}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>{line3Right}</span>
        </div>
      </div>
    </div>
  );
};

// An attribute's PREVIEW FACE: the first declared value whose served face is a word (controller
// ruling, 2026-09-18). The preview shows a ROW, so it must show what a row shows — the value's face
// („Gas", „Lieferant"), never the attribute's own settings-list label („Sparte"), which is a word
// no ticket ever wears. `null` when the tenant suppressed every value's face (a served `null` means
// "do not put this on a row"): there is then no honest pill to preview, and the kind is skipped
// here rather than given an invented one. Its CHECKBOX still stands — a tenant can serve a face
// later, and the preference outlives the vocabulary that was current when it was made.
const attributePreviewFace = (cm: ReturnType<typeof useCategoryMeta>, attributeName: string): string | null => {
  const declared = cm.attribute(attributeName);
  for (const value of declared && declared.values ? declared.values : []) {
    const face = cm.attributeFace(attributeName, value);
    if (face !== null) return face;
  }
  return null;
};

const RowPillPreview: React.FC<{ hidden: readonly string[]; kinds: readonly RowPillKind[]; testId: string }> = ({ hidden, kinds, testId }) => {
  const cm = useCategoryMeta();
  const sample = cm.categoryKeys[0];
  // part 7 fix loop, 2026-10-12: the preview wears the same primary paint as a list row, so a
  // tenant whose first served value is the unclassified one sees the tint here too.
  const sampleColor = sample ? cm.color(sample) : theme.statusStrip.unclassified; // the approved-as companion keeps the handling hue
  // The yes badge's word for the sample: the kind whose handling the sample is, where kinds are stated.
  const sampleKind = sample ? (Object.entries(cm.decisionKindHandling).find(([, h]) => h === sample)?.[0] as DraftKind | undefined) : undefined;
  const sampleApprovedWord = cm.statesApprovedKind && sampleKind ? approvedKindWord(sampleKind, cm.face) : approvedWord(cm.face);
  const sampleSwatch = sample ? cm.swatch(sample) : null;
  const samplePaint = primaryBandStyle(cm, sample, theme.statusStrip.unclassified);
  const sampleBadge: React.CSSProperties = sampleSwatch
    ? { color: sampleSwatch.ink, backgroundColor: sampleSwatch.fill, border: sampleSwatch.border, boxSizing: 'border-box' }
    : {};
  const infoPill = pillStyle('info', 'row');
  const axisPill = pillStyle('axis2', 'row');
  const has = (id: string) => kinds.some(k => k.id === id);
  // A faded wrapper rather than a faded child: the hint keeps its own register and metrics, and the
  // only thing the preference changes about it is how present it looks.
  const slot = (id: string, node: React.ReactNode, testIdSuffix = '') => has(id) && (
    <span
      key={`${id}${testIdSuffix}`}
      data-testid={`row-pill-preview-${id}${testIdSuffix}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: hidden.includes(id) ? FADED : 1, transition: 'opacity 0.15s ease' }}
    >
      {node}
    </span>
  );
  const secondarySample = cm.secondaryAxisKeys[0];
  // The sender pill's two specimens are the REAL SenderPill, mounted with invented registry rows
  // (part 7, 2026-10-11) — so the preview can never drift from the pill: same face
  // „Organisation · Art", same ink, same border. What the two states MEAN is the REGISTRY's fact
  // (R33a): a DASHED pill is an unconfirmed proposal, a SOLID one an established organisation, hence
  // two different invented names. The kind is the first declared sender_kind value whose served face
  // is a word, so the specimen speaks the tenant's own vocabulary.
  const previewKind = (cm.attribute('sender_kind')?.values ?? []).find(v => cm.attributeFace('sender_kind', v) !== null) ?? null;
  const senderSpecimen = (established: boolean) => (
    <SenderPill
      organisation={{
        id: established ? 'preview-established' : 'preview-proposed',
        name: established ? 'Beispiel Netz' : 'Neue Firma',
        shortName: null,
        kind: previewKind ?? '',
        status: established ? 'confirmed' : 'proposed',
      }}
      senderKind={previewKind}
      email={null}
    />
  );
  const attributeKinds = kinds.filter(k => k.id.startsWith(ATTRIBUTE_KIND_PREFIX));

  return (
    <div data-testid={testId} style={{ marginTop: theme.spacing.sm, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* Row A — a mail as it ARRIVES: the classification on row 2, the unconfirmed sender (dashed)
          and every info pill on row 3 left, nothing decided yet. ROW COMPOSITION v1 (part 7 fix
          loop, 2026-10-12): each kind sits on the line, and in the order, the list row draws it. */}
      <PreviewRow
        subject="Zählerstand zur Marktlokation gemeldet"
        stripValue={sample ?? null}
        stripPaint={samplePaint}
        line2={<>
          {sample && cm.rowLayout === 'strip_label' && (
            <span style={{ ...infoPill, fontWeight: 600, ...sampleBadge }}>
              {cm.label(sample)}
            </span>
          )}
          {slot('process_type', (
            <span style={axisPill}>
              {pillFace('axis2', secondarySample ? cm.secondaryAxisText(secondarySample) : cm.secondaryAxisNoun)}
            </span>
          ))}
          {slot('reanalysis', <ClerkTrace kind="reanalysis" count={2} />)}
        </>}
        line3Left={<>
          {slot('sender_organisation', senderSpecimen(false))}
          {/* The real FormPill, where the tenant serves form extraction (the kind exists only then). */}
          {slot('formular', <FormPill />)}
          {slot('frist_genannt', <FristGenanntPill quote="Bitte antworten Sie bis zum 15.11." />)}
          {slot('eskalationsstufe', <StufeChip n={2} variant="row" />)}
          {attributeKinds.map(kind => {
            const face = attributePreviewFace(cm, kind.id.slice(ATTRIBUTE_KIND_PREFIX.length));
            return face === null ? null : slot(kind.id, <span style={infoPill}>{face}</span>);
          })}
        </>}
        line3Right={null}
      />

      {/* Row B — the same kind of mail once DECIDED: the established sender (solid) on row 3 left,
          the approved-as badge and the clerk traces on row 3 right. */}
      <PreviewRow
        subject="Rückfrage zur letzten Abrechnung"
        stripValue={sample ?? null}
        stripPaint={samplePaint}
        line2={sample && cm.rowLayout === 'strip_label' && (
          <span style={{ ...infoPill, fontWeight: 600, ...sampleBadge }}>
            {cm.label(sample)}
          </span>
        )}
        line3Left={slot('sender_organisation', senderSpecimen(true), '-bestaetigt')}
        line3Right={<>
          {/* The yes badge, as the row draws it (DecisionBadges): „<Art> genehmigt" where the tenant
              states more than one Bearbeitung, else „Genehmigt" — never hideable, so never in a slot. */}
          {sample && (
            <DecisionBadge act="approved" hue={sampleColor} word={sampleApprovedWord} />
          )}
          {slot('kopiert', <ClerkTrace kind="kopiert" />)}
          {slot('bearbeitet', <ClerkTrace kind="bearbeitet" />)}
          {slot('eingang_bestaetigt', <ClerkTrace kind="eingang_bestaetigt" />)}
        </>}
      />
    </div>
  );
};

// ─── The usage survey ──────────────────────────────────────────────────────────────────────────
const UsageTable: React.FC<{ survey: RowPillSurvey | null; kinds: readonly RowPillKind[] }> = ({ survey, kinds }) => {
  if (survey === null) return null;
  const { accounts } = survey;
  return (
    // FADES IN when the fetch lands (house rule): the block is the last element on the tab, so
    // nothing below it moves when it arrives — but arriving is still an appearance, and an
    // appearance without a fade is a pop-in.
    <div data-testid="row-pill-usage" style={{ animation: 'stx-fadeIn 0.3s ease' }}>
      <div style={BLOCK_TITLE}>Nutzung</div>
      <p data-testid="row-pill-usage-intro" style={{ ...HELPER, margin: '0 0 8px' }}>
        Wie viele Konten den jeweiligen Hinweis eingeblendet lassen ({accounts} Konten mit eigener Einstellung).
      </p>
      {accounts === 0 ? (
        <p data-testid="row-pill-usage-empty" style={{ ...HELPER, margin: 0 }}>
          Bisher hat niemand eine eigene Auswahl gespeichert — alle folgen der Vorgabe.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {kinds.map(kind => {
            // A kind nobody hides may be absent from the response entirely — that is `off = 0`,
            // never "no data": the endpoint reports deviations, and no deviation is a real answer.
            const entry = survey.kinds.find(k => k.id === kind.id);
            const off = entry ? entry.off : 0;
            const on = accounts - off;
            const share = Math.round((on / accounts) * 100);
            return (
              <div key={kind.id} data-testid={`row-pill-usage-${kind.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontFamily: theme.font.family }}>
                <span style={{ flex: '0 0 40%', color: theme.colors.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><RowPillLabel label={kind.labelDe} /></span>
                <span style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: theme.colors.bgLight, overflow: 'hidden' }}>
                  <span data-testid="row-pill-usage-bar" style={{ display: 'block', height: '100%', width: `${share}%`, backgroundColor: theme.colors.navyMid }} />
                </span>
                <span style={{ flex: '0 0 90px', textAlign: 'right', color: theme.colors.textSecondary }}>{on} von {accounts}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── The section ───────────────────────────────────────────────────────────────────────────────
export interface RowPillSettingsProps {
  /** The tenant-default block and the survey are admin-only; „Meine Liste" is not. */
  canEditDefault: boolean;
  /** UI_ROW_PILLS_HIDDEN as the page's config draft holds it — a CSV of hidden ids. */
  defaultDraft: string;
  /** Whether the tenant has stated a default at all (a config row exists, blank included) or the
   *  draft now states one. False = the code default applies and the grid shows it. */
  defaultStated: boolean;
  onDefaultChange: (csv: string) => void;
  defaultDirty: boolean;
  onDefaultSave: () => void;
  onDefaultReset: () => void;
  /** The page's own save-in-flight flag, shared by every lane on it. */
  saving: boolean;
}

const RowPillSettings: React.FC<RowPillSettingsProps> = ({
  canEditDefault, defaultDraft, defaultStated, onDefaultChange, defaultDirty, onDefaultSave, onDefaultReset, saving,
}) => {
  const cm = useCategoryMeta();
  const rowPills = useRowPillVisibility();
  const kinds = useMemo(() => rowPillKinds(cm), [cm]);

  const [survey, setSurvey] = useState<RowPillSurvey | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingMine, setSavingMine] = useState(false);

  useEffect(() => {
    if (!canEditDefault) return;
    let live = true;
    // A survey that cannot be read is not worth a message of its own — the block simply does not
    // render, exactly as it does not for a clerk without the gate.
    getRowPillSurvey()
      .then(s => { if (live) setSurvey(s); })
      .catch(() => { if (live) setSurvey(null); });
    return () => { live = false; };
  }, [canEditDefault]);

  // The Vorgabe grid shows what a clerk without a preference actually gets: the tenant's stated
  // list (a blank row is a stated "hide nothing"), or — while the tenant states none — the CODE
  // default (DEFAULT_HIDDEN_ROW_PILLS, the Stufe pill). A grid that showed every box checked while
  // the Stufe pill ships hidden would lie; the helper line under it names the code default. The
  // first toggle turns the code default into a stated draft (the page's lane sees the key), so an
  // admin can also state "hide nothing" by un-hiding the last default kind and saving.
  const defaultHidden = useMemo(() => defaultStated ? parseHiddenRowPills(defaultDraft) : [...DEFAULT_HIDDEN_ROW_PILLS], [defaultDraft, defaultStated]);

  const store = useCallback(async (next: string[]) => {
    setError(''); setSuccess(''); setSavingMine(true);
    try {
      await rowPills.setHidden(next);
      setSuccess('Auswahl gespeichert.');
    } catch (err) {
      setError(`Auswahl nicht gespeichert: ${formatBackendError(err, 'Speichern')}`);
    }
    setSavingMine(false);
  }, [rowPills]);

  return (
    <div>
      <div style={SECTION_TITLE}>Hinweise in der Liste</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>

        {canEditDefault && (
          <div style={{
            padding: theme.spacing.sm, borderRadius: theme.borderRadius.sm,
            border: `1px solid ${defaultDirty ? theme.colors.warning : 'transparent'}`,
            backgroundColor: theme.colors.bgPage,
          }}>
            <div style={{ ...BLOCK_TITLE, display: 'flex', alignItems: 'center', gap: 8 }}>
              Vorgabe
              <span data-testid="row-pill-block-chip-vorgabe" style={overlayChipStyle('frozen')}>{pillFace('info', 'Vorgabe')}</span>
            </div>
            <KindGrid
              kinds={kinds}
              hidden={defaultHidden}
              block="Vorgabe"
              disabled={saving}
              onToggle={(id, visible) => onDefaultChange(formatHiddenRowPills(withRowPillHidden(kinds, defaultHidden, id, visible)))}
            />
            <p style={HELPER}>
              Abgewählte Hinweise erscheinen nicht in der Ticketliste. Jede Sachbearbeiterin kann für
              sich davon abweichen.
              {!defaultStated && DEFAULT_HIDDEN_ROW_PILLS.length > 0 && (
                <span data-testid="row-pill-default-note">{' '}Ohne eigene Vorgabe gilt die Voreinstellung: {kinds.filter(k => DEFAULT_HIDDEN_ROW_PILLS.includes(k.id)).map(k => k.labelDe).join(', ')} ausgeblendet.</span>
              )}
            </p>
            <RowPillPreview hidden={defaultHidden} kinds={kinds} testId="row-pill-preview-vorgabe" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm, marginTop: theme.spacing.sm }}>
              {defaultDirty && (
                <button aria-label="Zurücksetzen (Hinweise-Vorgabe)" onClick={onDefaultReset} style={btnStyle('ghost')}>
                  Zurücksetzen
                </button>
              )}
              <button
                aria-label="Speichern (Hinweise-Vorgabe)"
                onClick={onDefaultSave}
                disabled={!defaultDirty || saving}
                style={{
                  ...btnStyle('primary'),
                  opacity: (!defaultDirty || saving) ? 0.5 : 1,
                  cursor: (!defaultDirty || saving) ? 'default' : 'pointer',
                }}
              >{saving ? 'Speichern...' : 'Speichern'}</button>
            </div>
          </div>
        )}

        <div style={{ padding: theme.spacing.sm, borderRadius: theme.borderRadius.sm, backgroundColor: theme.colors.bgPage }}>
          <div style={{ ...BLOCK_TITLE, display: 'flex', alignItems: 'center', gap: 8 }}>
            Meine Liste
            <span data-testid="row-pill-block-chip-meine-liste" style={overlayChipStyle('frozen')}>{pillFace('info', 'Nur ich')}</span>
          </div>
          <KindGrid
            kinds={kinds}
            hidden={rowPills.hidden}
            block="Meine Liste"
            disabled={savingMine}
            onToggle={(id, visible) => { void store(withRowPillHidden(kinds, rowPills.hidden, id, visible)); }}
          />
          <p style={HELPER}>
            Gilt nur für Ihre eigene Ansicht und wird sofort gespeichert.
          </p>
          <RowPillPreview hidden={rowPills.hidden} kinds={kinds} testId="row-pill-preview-meine-liste" />
          {/* Fixed-height, always mounted, polite — the slot is already in the DOM and already
              sized, so what lands in it announces without shifting anything (Layout-Stability). */}
          <div role="status" data-testid="row-pills-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch', marginTop: theme.spacing.sm }}>
            {error ? (
              <div style={{ ...feedbackStyle(true), animation: 'stx-fadeIn 0.2s ease' }}>{error}</div>
            ) : success ? (
              <div style={{ ...feedbackStyle(false), animation: 'stx-fadeIn 0.2s ease' }}>{success}</div>
            ) : null}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {/* There is no DELETE route by ruling: „zurücksetzen" PUTs the DEFAULT'S OWN LIST, so
                this clerk keeps a stored row that happens to agree with the tenant. A later change
                to the default therefore does not follow them — which is the honest reading of a
                clerk who once made a choice. */}
            <button
              onClick={() => { void store([...rowPills.tenantDefault]); }}
              disabled={savingMine}
              style={{ ...btnStyle('ghost'), opacity: savingMine ? 0.5 : 1 }}
            >Auf Vorgabe zurücksetzen</button>
          </div>
        </div>

        {canEditDefault && <UsageTable survey={survey} kinds={kinds} />}
      </div>
    </div>
  );
};

export default RowPillSettings;
